import { create } from 'zustand';

import { USER_ROLE } from '@/constant/userConstant';
import {
  Assignment,
  DeployedCodeUpdate,
  DeveloperBoard,
  Help1,
  KeyVertical,
  ManageAccounts,
  Storage,
} from '@/package';
import { StickyNote2 } from '@/package';
import type { SideBarMenuItem, User } from '@/type';

const createMenu = (
  name: string,
  path: string,
  Icon: React.FunctionComponent<{ className?: string }>,
  disabled?: boolean,
): SideBarMenuItem => ({
  disabled: disabled ?? false,
  name,
  path,
  Icon,
});

const MENULIST_BY_ROLE: {
  [K in USER_ROLE]: {
    top: SideBarMenuItem[];
    bottom: SideBarMenuItem[];
  };
} = {
  [USER_ROLE.SITE_ADMIN]: {
    top: [
      createMenu('Collection', '/collection', Storage),
      createMenu('Annotation', '/project', StickyNote2),
      createMenu('Modeling', '/modeling', DeveloperBoard),
      createMenu('Deployment', '/deployment', DeployedCodeUpdate),
      createMenu('Tasks', '/my-work', Assignment),
    ],
    bottom: [
      createMenu('API Keys', '/api-key', KeyVertical),
      createMenu('Settings', '/member', ManageAccounts),
      createMenu('Help', '/help', Help1),
    ],
  },
  get [USER_ROLE.MANAGER]() {
    return this.SiteAdmin;
  },
  get [USER_ROLE.ADMIN]() {
    return this.SiteAdmin;
  },
  [USER_ROLE.WORKER]: {
    top: [createMenu('Your works', '/my-work', Assignment)],
    bottom: [
      createMenu('Settings', '/member', ManageAccounts),
      createMenu('Help', '/help', Help1, true),
    ],
  },
  [USER_ROLE.GUEST]: {
    top: [],
    bottom: [],
  },
};

const INIT_STATE = {
  menuList: {
    top: [],
    bottom: [],
  },
};

type SideBarState = {
  menuList: {
    top: SideBarMenuItem[];
    bottom: SideBarMenuItem[];
  };
};

type SideBarActions = {
  clean(): void;

  disableMenu(position: 'top' | 'bottom', name: SideBarMenuItem['name']): void;

  enableMenu(position: 'top' | 'bottom', name: SideBarMenuItem['name']): void;

  setupMenuList(role: User['role']): void;
};

const useSideBarStore = create<SideBarState & SideBarActions>((set, get) => {
  const setDisabledMenu = (
    position: 'top' | 'bottom',
    name: SideBarMenuItem['name'],
    disabled: boolean,
  ) => {
    const menuList = get().menuList;

    if (!menuList) return;

    set({
      menuList: {
        ...menuList,
        [position]: menuList[position].map(item => {
          if (item.name !== name) return item;
          return item.name !== name ? item : { ...item, disabled };
        }),
      },
    });
  };

  return {
    ...INIT_STATE,

    clean() {
      set({ ...INIT_STATE });
    },

    disableMenu: (position, name) => setDisabledMenu(position, name, true),

    enableMenu: (position, name) => setDisabledMenu(position, name, false),

    setupMenuList(role) {
      set({
        menuList: MENULIST_BY_ROLE[role],
      });
    },
  };
});

export default useSideBarStore;
